import { z } from 'zod';
import { TipoAcumulacionEnum } from 'repository/timekeeper/@types/TypeAccrualEnum';
import { normalizeString } from 'utils/Validations';

/**
 * Interfaz que define los valores del formulario para códigos de acumulación
 */
export interface FormValues {
    /** ID opcional del código de acumulación (para edición) */
    id?: number | string;
    /** Nombre del código de acumulación */
    name: string;
    /** Abreviatura del código */
    abbreviation: string;
    /** Tipo de acumulación (días, horas, etc.) */
    type_accrual: TipoAcumulacionEnum;
    /** Horas por día en formato HH:mm (requerido para tipo DIAS) */
    hour_per_day?: string | null;
    /** Indica si se permiten ediciones manuales */
    allow_manual_edits: boolean;
    /** Indica si se muestra el código */
    show_code: boolean;
}

/**
 * Crea un schema de validación para códigos de acumulación usando Zod
 *
 * @param params - Parámetros de configuración
 * @param params.t - Función de traducción para mensajes de error
 * @param params.rows - Array de registros existentes para validar duplicados
 * @param params.initialValues - Valores iniciales del formulario (opcional, para edición)
 * @param params.readOnly - Indica si el formulario está en modo solo lectura
 * @returns Schema de validación de Zod configurado
 */
export function createAccrualCodeSchema(params: {
    t: (key: string, options?: any) => string;
    rows: any[];
    initialValues?: Partial<FormValues>;
    readOnly?: boolean;
}) {
    const { t, rows, initialValues, readOnly = false } = params;

    // Crea un enum de Zod con todos los valores posibles de TipoAcumulacionEnum
    const tipoAccrualSchema = z.enum(Object.values(TipoAcumulacionEnum) as [string, ...string[]]);

    return z
        .object({
            // Validación del nombre: requerido, máximo 100 caracteres
            name: z
                .string()
                .trim()
                .regex(/^[a-zA-Z0-9\s]+$/, t('general.zod.string.invalid_characters') as string) // Solo letras, números y espacios
                .min(3, t('general.zod.string.min_chars', { min: 3 }) as string)
                .max(100, t('general.zod.string.max_chars', { max: 100 }) as string),
            // Validación de la abreviatura: requerido, máximo 100 caracteres
            abbreviation: z
                .string()
                .trim()
                .min(3, t('general.zod.string.min_chars', { min: 3 }) as string)
                .max(3, t('general.zod.string.max_chars', { max: 3 }) as string),
            // Validación del tipo de acumulación usando el enum
            type_accrual: tipoAccrualSchema,
            // Se valida condicionalmente en superRefine cuando type_accrual es DIAS
            hour_per_day: z.string().nullable().optional(),
            allow_manual_edits: z.boolean().default(false),
            show_code: z.boolean().default(false),
        })
        .superRefine((data, ctx) => {
            // Validación 1: Verificar que no exista otro código con el mismo nombre
            const normalizedInput = normalizeString(data.name);
            const currentId = initialValues?.id;

            // Busca registros duplicados excluyendo el registro actual (si se está editando)
            const existsInRows = rows?.some((r: any) => {
                const sameName = normalizeString(r?.name ?? '') === normalizedInput;
                const isSameRecord = currentId !== undefined && r?.id === currentId;
                return sameName && !isSameRecord;
            });

            // Si existe un duplicado y no está en modo solo lectura, agregar error
            if (!readOnly && existsInRows) {
                ctx.addIssue({
                    path: ['name'],
                    code: 'custom',
                    message: t('accruals.accrual_codes.dialog.alerts.name_exist') as string,
                });
            }

            // Validación 2: si el tipo es DIAS, horas por día es obligatorio y debe ser HH:mm entre 00:01 y 24:00
            if (data.type_accrual === TipoAcumulacionEnum.DIAS) {
                const value = (data.hour_per_day ?? '').trim();

                if (!value || value.includes('_')) {
                    ctx.addIssue({
                        path: ['hour_per_day'],
                        code: 'custom',
                        message: t('general.zod.time_format_hm.required') as string,
                    });
                    return;
                }

                if (!/^\d{2}:\d{2}$/.test(value)) {
                    ctx.addIssue({
                        path: ['hour_per_day'],
                        code: 'custom',
                        message: t('general.zod.time_format_hm.invalid') as string,
                    });
                    return;
                }

                const [hours, minutes] = value.split(':').map(Number);
                const total = hours * 60 + minutes;
                if (total < 1 || total > 1440 || minutes > 59 || (hours === 24 && minutes > 0)) {
                    ctx.addIssue({
                        path: ['hour_per_day'],
                        code: 'custom',
                        message: t('general.zod.time_format_hm.invalid') as string,
                    });
                }
            }
        });
}
